'use client';

import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { Card, CardContent } from '../ui/card';
import type { Bookmark } from '@/lib/bookmark/types';
import { DragHandle } from './drag-handle';
import { HandleWorkflowDropdown } from './handle-workflow-dropdown';
import Link from 'next/link';
import { useMemo, type CSSProperties } from 'react';
import { Icons } from '../icons';
import { cn } from '@/lib/cn';
import { useWorkflows } from '@/providers/workflow-provider';
import { usePathname, useSearchParams } from 'next/navigation';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { DEFAULT_BOOKMARK_COLOR } from '@/lib/bookmark/constants';
import { getBookmarkPath } from '@/lib/bookmark/utils/bookmark-utils';
import { useBookmarkCount } from '@/lib/bookmark/hooks/use-bookmark-count';

interface WorkflowBookmarkProps {
  readonly bookmark: Bookmark;
  readonly isEditMode: boolean;
  readonly workEngagementId?: string | null;
  readonly dragHandleProps?: DraggableProvidedDragHandleProps | null;
  readonly variant?: 'default' | 'banner';
}

export const WorkflowBookmark = ({
  isEditMode,
  bookmark,
  workEngagementId = null,
  dragHandleProps,
  variant = 'default',
}: WorkflowBookmarkProps) => {
  const {
    isActionModalOpen,
    setCurrentWorkflowFolder,
    setCurrentWorkflowBookmark,
    userWorkflows,
    workEngagementWorkflows,
  } = useWorkflows();

  const folders = useMemo(() => {
    return [
      ...(userWorkflows?.folders ?? []),
      ...(workEngagementWorkflows?.flatMap((w) => w.folders) ?? []),
    ];
  }, [userWorkflows, workEngagementWorkflows]);

  const url = getBookmarkPath(bookmark);

  return url ? (
    <Link
      href={url}
      onClick={(e) => {
        e.stopPropagation();

        if (isActionModalOpen) {
          e.preventDefault();
          return;
        }

        const folder = folders?.find((folder) =>
          folder.child_bookmarks?.some(
            (b) => b.client_id === bookmark.client_id
          )
        );

        if (folder) {
          setCurrentWorkflowFolder(folder);
        }

        setCurrentWorkflowBookmark(bookmark);
      }}
    >
      <BookmarkCard
        variant={variant}
        isEditMode={isEditMode}
        bookmark={bookmark}
        dragHandleProps={dragHandleProps}
        workEngagementId={workEngagementId}
      />
    </Link>
  ) : (
    <BookmarkCard
      variant={variant}
      isEditMode={isEditMode}
      bookmark={bookmark}
      dragHandleProps={dragHandleProps}
      workEngagementId={workEngagementId}
    />
  );
};

interface BookmarkCountProps {
  readonly bookmark: Bookmark;
  readonly variant: 'default' | 'banner';
}

const BookmarkCount = ({ bookmark, variant }: BookmarkCountProps) => {
  const { count, isLoading } = useBookmarkCount(bookmark);

  if (isLoading) {
    return <Icons.loader className='size-4 animate-spin' />;
  }

  const displayCount =
    variant === 'banner' && typeof count === 'number' && count > 9999
      ? '9999+'
      : count;

  return (
    <span
      className={cn(
        typeof count === 'number' &&
          (count > 0
            ? 'text-[color-mix(in_srgb,var(--wf-color)_70%,black)] dark:text-[color-mix(in_srgb,var(--wf-color)_70%,white)]'
            : 'text-muted-foreground')
      )}
    >
      {displayCount}
    </span>
  );
};

interface BookmarkCardProps {
  readonly variant: 'default' | 'banner';
  readonly isEditMode: boolean;
  readonly bookmark: Bookmark;
  readonly dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  readonly workEngagementId: string | null;
}

const BookmarkCard = ({
  variant,
  isEditMode,
  bookmark,
  dragHandleProps,
  workEngagementId,
}: BookmarkCardProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const url = getBookmarkPath(bookmark);

  const currentUrl = useMemo(() => {
    const queryString = searchParams.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  }, [pathname, searchParams]);

  const isViewingBookmark = (url: string) => {
    return decodeURIComponent(url) === decodeURIComponent(currentUrl);
  };

  return (
    <Card
      initial={false}
      className={cn(
        'flex w-[180px] cursor-pointer flex-row items-stretch gap-0 rounded-none border-0 border-t-4 border-t-[var(--wf-color)] p-0',
        'bg-[color-mix(in_srgb,var(--wf-color)_8%,transparent)]',
        '[&:not(:has(button:hover))]:hover:bg-[color-mix(in_srgb,var(--wf-color)_18%,transparent)]',
        {
          'h-[84px] justify-center': variant === 'default',
          'h-[34px] border-t-3': variant === 'banner',
          'bg-[color-mix(in_srgb,var(--wf-color)_20%,transparent)]':
            isViewingBookmark(url),
          'w-[220px]': isEditMode,
        }
      )}
      style={
        {
          '--wf-color': bookmark.color ?? DEFAULT_BOOKMARK_COLOR,
        } as CSSProperties
      }
    >
      <DragHandle dragHandleProps={dragHandleProps} />
      <CardContent className='flex w-full items-stretch justify-between gap-4'>
        <div
          className={cn('flex min-w-0 flex-row', {
            'flex-col p-2': variant === 'default',
            'flex-row items-center gap-1.5 p-2': variant === 'banner',
          })}
        >
          {variant === 'banner' && (
            <div className='flex items-center justify-center text-[12px] font-semibold'>
              <BookmarkCount bookmark={bookmark} variant={variant} />
            </div>
          )}
          {variant === 'default' && (
            <div className='text-lg font-semibold'>
              <BookmarkCount bookmark={bookmark} variant={variant} />
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn('min-w-0', {
                  'line-clamp-2 text-xs': variant === 'default',
                  'line-clamp-1 text-[12px]': variant === 'banner',
                })}
              >
                {bookmark.name}
              </span>
            </TooltipTrigger>
            <TooltipContent className='max-w-[400px]'>
              {bookmark.name}
            </TooltipContent>
          </Tooltip>
        </div>
        <HandleWorkflowDropdown
          workEngagementId={workEngagementId}
          type='bookmark'
          currentItem={bookmark}
          isEditMode={isEditMode}
        />
      </CardContent>
    </Card>
  );
};
